class RmBannerSlider extends HTMLElement {
  constructor() {
    super();

    this.initSwiper();
    this.addSafariResizeWatcher();
    
    // Store references for widget management
    this.reinitializeTimer = null;
    this.masterWidget = null;
    this.observerActive = false;
  }

  initSwiper() {

    this.swiper = new Swiper('.rm-banner-slider-swiper', {
      
      direction: 'horizontal',
      loop: true,
      speed: 1500,
      autoplay: {
        delay: 3000,
      },

      autoHeight: true,

      // preloadImages: false,
      // lazy: {
      //   loadPrevNext: true,
      // 	loadPrevNextAmount: 1,
      // },

      effect: 'fade',
      fadeEffect: {
        crossFade: true
      },
    
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      },

      pagination: {
        el: ".swiper-pagination",
      },

      // Add slide change event listeners to handle Reviews.io widgets
      on: {
        init: () => {
          // Initialize widgets when swiper is ready
          setTimeout(() => {
            this.initializeAllWidgets();
          }, 500);
        },
        slideChangeTransitionEnd: () => {
          this.ensureWidgetsVisible();
        },
        slideChange: () => {
          this.ensureWidgetsVisible();
        }
      }
    
    });

    this.addObserver();

  }

  // Initialize all widgets and create a master template
  initializeAllWidgets() {
    const allSlides = document.querySelectorAll('.rm-banner-slider .swiper-slide');
    let workingWidget = null;

    // First, let Reviews.io script process all existing widgets
    this.loadReviewsScript(() => {
      setTimeout(() => {
        // Find a working widget to use as template
        allSlides.forEach(slide => {
          const widget = slide.querySelector('.reviews-io-rating-bar-widget');
          if (widget && this.isWidgetWorking(widget)) {
            workingWidget = widget;
          }
        });

        if (workingWidget) {
          this.masterWidget = workingWidget.cloneNode(true);
          this.ensureAllSlidesHaveWidgets();
        } else {
          // If no working widget found, try to create one
          this.createMasterWidget();
        }
      }, 1000);
    });
  }

  // Ensure all slides have working widgets
  ensureAllSlidesHaveWidgets() {
    const allSlides = document.querySelectorAll('.rm-banner-slider .swiper-slide');
    
    allSlides.forEach(slide => {
      const existingWidget = slide.querySelector('.reviews-io-rating-bar-widget');
      
      if (!existingWidget || !this.isWidgetWorking(existingWidget)) {
        if (this.masterWidget) {
          const reviewsContainer = slide.querySelector('.rm-banner-slider-content');
          if (reviewsContainer) {
            // Remove broken widget if exists and remember its position
            let shouldPlaceAbove = true; // default to above
            if (existingWidget) {
              shouldPlaceAbove = this.isWidgetAboveText(existingWidget, reviewsContainer);
              existingWidget.remove();
            } else {
              // Check if any other slide has a widget and determine position from that
              shouldPlaceAbove = this.determineWidgetPosition();
            }
            
            // Clone and insert working widget in correct position
            const newWidget = this.masterWidget.cloneNode(true);
            this.insertWidgetAtCorrectPosition(newWidget, reviewsContainer, shouldPlaceAbove);
          }
        }
      }
    });
  }

  // Determine if widget should be placed above text elements
  isWidgetAboveText(widget, container) {
    const textElements = container.querySelectorAll('.rm-banner-slider-text-1, .rm-banner-slider-text-2');
    if (textElements.length === 0) return true;
    
    const firstTextElement = textElements[0];
    const widgetPosition = Array.from(container.children).indexOf(widget);
    const textPosition = Array.from(container.children).indexOf(firstTextElement);
    
    return widgetPosition < textPosition;
  }

  // Determine widget position from existing slides or section settings
  determineWidgetPosition() {
    // First, try to find any existing working widget to determine position
    const allSlides = document.querySelectorAll('.rm-banner-slider .swiper-slide');
    for (let slide of allSlides) {
      const widget = slide.querySelector('.reviews-io-rating-bar-widget');
      const container = slide.querySelector('.rm-banner-slider-content');
      if (widget && container && this.isWidgetWorking(widget)) {
        return this.isWidgetAboveText(widget, container);
      }
    }
    
    // Fallback: try to determine from the master widget's original position
    if (this.masterWidget && this.masterWidget.parentNode) {
      return this.isWidgetAboveText(this.masterWidget, this.masterWidget.parentNode);
    }
    
    // Default to below (as per schema default)
    return false;
  }

  // Insert widget at correct position (above or below text)
  insertWidgetAtCorrectPosition(widget, container, shouldPlaceAbove) {
    const textElements = container.querySelectorAll('.rm-banner-slider-text-1, .rm-banner-slider-text-2');
    
    if (shouldPlaceAbove) {
      // Place above text elements
      if (textElements.length > 0) {
        container.insertBefore(widget, textElements[0]);
      } else {
        // No text elements, place at beginning
        container.insertBefore(widget, container.firstChild);
      }
    } else {
      // Place below text elements
      if (textElements.length > 0) {
        const lastTextElement = textElements[textElements.length - 1];
        if (lastTextElement.nextSibling) {
          container.insertBefore(widget, lastTextElement.nextSibling);
        } else {
          container.appendChild(widget);
        }
      } else {
        // No text elements, place at beginning
        container.insertBefore(widget, container.firstChild);
      }
    }
  }

  // Replace widget in specific slide
  replaceWidgetInSlide(slide) {
    const existingWidget = slide.querySelector('.reviews-io-rating-bar-widget');
    const reviewsContainer = slide.querySelector('.rm-banner-slider-content');
    
    if (!reviewsContainer || !this.masterWidget) return;

    // Determine correct position before removing existing widget
    let shouldPlaceAbove = true;
    if (existingWidget) {
      shouldPlaceAbove = this.isWidgetAboveText(existingWidget, reviewsContainer);
      existingWidget.remove();
    } else {
      shouldPlaceAbove = this.determineWidgetPosition();
    }

    const newWidget = this.masterWidget.cloneNode(true);
    this.insertWidgetAtCorrectPosition(newWidget, reviewsContainer, shouldPlaceAbove);
  }

  // Create a master widget from scratch
  createMasterWidget() {
    // Find the first widget with proper attributes
    const firstWidget = document.querySelector('.reviews-io-rating-bar-widget');
    if (!firstWidget) return;

    const widgetId = firstWidget.getAttribute('widget-id');
    const storeName = firstWidget.getAttribute('data-store-name');
    const lang = firstWidget.getAttribute('lang') || 'en';

    if (!widgetId || !storeName) return;

    // Create a temporary container to initialize the widget
    const tempContainer = document.createElement('div');
    tempContainer.style.position = 'absolute';
    tempContainer.style.top = '-9999px';
    tempContainer.style.left = '-9999px';
    tempContainer.innerHTML = `
      <div class="reviews-io-rating-bar-widget"
        widget-id="${widgetId}"
        data-store-name="${storeName}"
        lang="${lang}">
      </div>
    `;
    
    document.body.appendChild(tempContainer);

    // Load script and initialize
    this.loadReviewsScript(() => {
      setTimeout(() => {
        const tempWidget = tempContainer.querySelector('.reviews-io-rating-bar-widget');
        if (this.isWidgetWorking(tempWidget)) {
          this.masterWidget = tempWidget.cloneNode(true);
          document.body.removeChild(tempContainer);
          this.ensureAllSlidesHaveWidgets();
        } else {
          document.body.removeChild(tempContainer);
        }
      }, 1000);
    });
  }

  // Ensure widgets are visible in current slide
  ensureWidgetsVisible() {
    if (this.reinitializeTimer) {
      clearTimeout(this.reinitializeTimer);
    }
    
    this.reinitializeTimer = setTimeout(() => {
      const activeSlide = document.querySelector('.rm-banner-slider .swiper-slide-active');
      if (!activeSlide) return;

      const widget = activeSlide.querySelector('.reviews-io-rating-bar-widget');
      if (!widget || !this.isWidgetWorking(widget)) {
        if (this.masterWidget) {
          this.replaceWidgetInSlide(activeSlide);
        }
      }
    }, 200);
  }

  // Check if widget is working properly
  isWidgetWorking(widget) {
    if (!widget) return false;
    
    const hasContent = widget.innerHTML && widget.innerHTML.trim().length > 0;
    const isVisible = widget.offsetHeight > 0 && widget.offsetWidth > 0;
    const hasIframe = widget.querySelector('iframe') !== null;
    const hasRatings = widget.querySelector('[class*="rating"], [class*="star"], [class*="review"]') !== null;
    
    return hasContent && isVisible && (hasIframe || hasRatings);
  }

  // Load Reviews.io script
  loadReviewsScript(callback) {
    // Check if script is already loaded
    if (window.ReviewsWidget || window.ReviewsIO || document.querySelector('script[src*="rating-bar.js"]')) {
      if (callback) callback();
      return;
    }

    const script = document.createElement('script');
    script.src = '//widget.reviews.io/modern-widgets/rating-bar.js';
    script.async = true;
    
    script.onload = () => {
      if (callback) callback();
    };

    script.onerror = () => {
      console.warn('Failed to load Reviews.io script');
      if (callback) callback();
    };

    document.head.appendChild(script);
  }

  addObserver() {
    const section = document.querySelector('.rm-banner-slider-sect-wrap');
    if(section == null) return;

    const observer = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if(entry.isIntersecting) {
          document.body.classList.add('state--slider-visible');
          document.body.classList.remove('state--slider-not-visible');
          // Initialize widgets when slider becomes visible
          if (!this.observerActive) {
            this.observerActive = true;
            setTimeout(() => {
              this.initializeAllWidgets();
            }, 300);
          }
        } else {
          document.body.classList.add('state--slider-not-visible');
          document.body.classList.remove('state--slider-visible');
        }
      })
    }, { root: null, rootMargin: '0px', threshold: 0.4 });

    observer.observe(section);
  }

  addSafariResizeWatcher() {
    let isSafari =
    navigator.vendor &&
    navigator.vendor.indexOf("Apple") > -1 &&
    navigator.userAgent &&
    navigator.userAgent.indexOf("CriOS") == -1 &&
    navigator.userAgent.indexOf("FxiOS") == -1;

    if (isSafari) {
      // Trigger Webkit engine after browser resize (Safari)
      // https://github.com/ausi/respimagelint/issues/31#issuecomment-420355534
      // Needed so the different image sizes load correctly on window resize in Safari
      window.addEventListener("resize", function() {
        let imgs = this.querySelectorAll("picture img");
        for (let i = 0; i < imgs.length; i++) {
          imgs[i].src += "";
        }
      }.bind(this));
    }
  }
  
  // Cleanup method
  cleanup() {
    if (this.reinitializeTimer) {
      clearTimeout(this.reinitializeTimer);
      this.reinitializeTimer = null;
    }
    
    if (this.swiper) {
      this.swiper.destroy();
      this.swiper = null;
    }
    
    this.masterWidget = null;
    this.observerActive = false;
  }

  // Called when element is removed from DOM
  disconnectedCallback() {
    this.cleanup();
  }
}
  
customElements.define('rm-banner-slider', RmBannerSlider);